import { randomString } from './random';
import metadata from 'libphonenumber-js/metadata.full.json';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import dedent from 'dedent';

export function addCustomSchemas(customDataSchemas) {
  if (!Array.isArray(customDataSchemas) || customDataSchemas.length === 0) {
    return [];
  }

  return customDataSchemas.map((schema) => ({
    id: randomString(16),
    title: schema?.title,
    icon: schema?.left_menu_icon,
    link: `/c/${schema?.id || schema?._id || schema?.machine_name}`,
    description: schema?.description,
    is_hidden: false,
    is_admin: false,
    children: [],
  }));
}

export function addCustomForms(customForms) {
  if (!Array.isArray(customForms) || customForms.length === 0) {
    return [];
  }

  return customForms.map((form) => ({
    id: randomString(16),
    title: form?.title,
    icon: form?.left_menu_icon,
    link: `/cf/${form?.machine_name}`,
    description: form?.description,
    is_hidden: false,
    is_admin: false,
    children: [],
  }));
}

export function getTableQuery(userDetails) {
  return {
    startRow: 0,
    endRow: 1000,
    filterModel: {
      show_in_left_menu: {
        filterType: 'set',
        values: [true],
      },
      user_id: {
        filterType: 'set',
        values: [userDetails?.user_id, null, ''],
      },
    },
    sortModel: [],
    fields: [
      'id',
      'title',
      'machine_name',
      'show_in_left_menu',
      'left_menu_icon',
      'description',
    ],
  };
}

export const findMatchingMenu = (pathname, allMenuItems) => {
  let bestMatch = null;

  for (const item of allMenuItems) {
    if (pathname === item.link || pathname.startsWith(item.link + '/')) {
      if (!bestMatch || item.link.length > bestMatch.link.length) {
        bestMatch = {
          menuId: item.id,
          subMenuId: null,
          link: item.link,
          children: item.children,
        };
      }
    }

    if (item.children && item.children.length > 0) {
      for (const child of item.children) {
        if (pathname === child.link || pathname.startsWith(child.link + '/')) {
          if (!bestMatch || child.link.length > bestMatch.link.length) {
            bestMatch = {
              menuId: item.id,
              subMenuId: child.id,
              link: child.link,
            };
          }
        }
      }
    }
  }

  return bestMatch
    ? { menuId: bestMatch?.menuId, subMenuId: bestMatch?.subMenuId }
    : { menuId: 'dashboard', subMenuId: null };
};

export const hexToRgba = (hex, alpha) => {
  const [r, g, b] = hex
    .replace('#', '')
    .match(/.{1,2}/g)
    .map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function filterMenuByModules(
  menuItems,
  userModules = {},
  isAdmin = false
) {
  return menuItems
    .map((item) => {
      const children = item.children?.length
        ? filterMenuByModules(item.children, userModules, isAdmin)
        : [];

      const isVisible =
        (!item.module || userModules?.[item.module] === true) &&
        !item.is_hidden &&
        (!item.is_admin || isAdmin);

      if (!isVisible && children.length === 0) return null;

      return {
        ...item,
        children,
      };
    })
    .filter(Boolean);
}

export function getCountryOptions() {
  const countries = getCountries();

  return countries.map((countryCode) => {
    const callingCode = getCountryCallingCode(countryCode, metadata);
    const flag = getCountryFlagEmoji(countryCode);
    const label = `${flag} ${getCountryName(countryCode)} (+${callingCode})`;

    return {
      label,
      value: countryCode,
      code: `+${callingCode}`,
    };
  });
}

export function getCountryFlagEmoji(countryCode) {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

export function getCountryName(code) {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  return regionNames.of(code);
}

export function titleCase(str) {
  if (typeof str !== 'string') {
    return '';
  }
  return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function toRGBAWithAlpha(color, alpha = 0.2) {
  let r, g, b;

  if (color.startsWith('#')) {
    // Handle shorthand hex (#abc)
    if (color.length === 4) {
      r = parseInt(color[1] + color[1], 16);
      g = parseInt(color[2] + color[2], 16);
      b = parseInt(color[3] + color[3], 16);
    }
    // Handle full hex (#aabbcc)
    else if (color.length === 7) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    } else {
      throw new Error('Invalid hex color format');
    }
  } else if (color.startsWith('rgb(')) {
    const [rStr, gStr, bStr] = color
      .replace('rgb(', '')
      .replace(')', '')
      .split(',')
      .map((s) => parseInt(s.trim(), 10));
    r = rStr;
    g = gStr;
    b = bStr;
  } else {
    throw new Error('Unsupported color format');
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function generateVoicePluginCodeSnippet(clientToken, frontendConfig) {
  const pluginSourceUrl = `${frontendConfig?.frontend?.plugin_root?.replace(/\/$/, '')}/static/js/plugin.js`;

  return dedent(`
    <!-- Start of Prospera Labs Voice Chat Plugin Code -->

    <script>
      (function() {
        var w = window;
        var d = document;
        var s = d.createElement('script');
        s.src = '${pluginSourceUrl}';
        s.id = 'plugin-${clientToken}';
        s.onload = function() {
          w.VoiceChat.init({
            clientToken: '${clientToken}'
          });
        };
        d.body.appendChild(s);
      })();
    </script>

    <!-- End of plugin code -->
  `);
}

export function generatePluginEmailBody({
  clientToken,
  frontendConfig,
  teamMemberName,
  userMessage,
}) {
  return dedent(`
    Hi ${teamMemberName || ''},

    You're invited to install the Prospera Labs Voice Chat Plugin on your website.
    This plugin enables seamless voice support directly in your frontend experience.

    ${userMessage ? `Message from your teammate:\n\n${userMessage}\n` : ''}

    To install the plugin, copy and paste the code snippet below into your website's HTML, just before the closing </body> tag:

    ------------------------
    ${generateVoicePluginCodeSnippet(clientToken, frontendConfig)}
    ------------------------

    If you need help setting this up, just reply to this email.

    Best regards,

    Prospera Labs
  `);
}

export function generatePluginEmailBodyForBackend({
  clientToken,
  frontendConfig,
  teamMemberName,
  userMessage,
}) {
  const pluginSourceUrl = `${frontendConfig?.frontend?.plugin_root?.replace(/\/$/, '')}/static/js/plugin.js`;

  return dedent(`
    <p>Hi ${teamMemberName || ''},</p>

    <p>You're invited to install the Prospera Labs Voice Chat Plugin on your website.<br>
    This plugin enables seamless voice support directly in your frontend experience.</p>

    ${userMessage ? `<p>Message from your teammate:</p><p>${userMessage}</p>` : ''}

    <p>To install the plugin, copy and paste the code snippet below into your website's HTML, just before the closing <code>&lt;/body&gt;</code> tag:</p>

    <pre>
    &lt;!-- Start of Prospera Labs Voice Chat Plugin Code --&gt;

      &lt;script&gt;
        (function() {
          var w = window;
          var d = document;
          var s = d.createElement('script');
          s.src = '${pluginSourceUrl}';
          s.id = 'plugin-${clientToken}';
          s.onload = function() {
            w.VoiceChat.init({
              clientToken: '${clientToken}'
            });
          };
          d.body.appendChild(s);
        })();
      &lt;/script&gt;

    &lt;!-- End of plugin code --&gt;
    </pre>

    If you need help setting this up, just reply to this email.

    Best regards,

    Prospera Labs
  `);
}
